<template>
  <draggable-resizer
    :id="elId"
    class="radar-grid-object-camera-container-wrapper"
  >
    <div class="radar-grid-object-camera-container">
      <div class="rg-camera-header">
        <span class="close-btn" @click="onClickCloseBtn"></span>
      </div>
      <div class="rg-camera-body">
        <rtsp-video-player
          v-if="isRtspPlayerVisible"
          ref="rtspVideoPlayer"
          :rtspCanvasId="rtspCanvasId"
          :rtspSrc="rtspSrc"
          @playing="playing"
          @playError="handleRtspPlayError"
          @connectionRestored="handleConnectionRestored"
          @disconnectDetection="handleDisconnectDetection"
        ></rtsp-video-player>
        <transition name="camera-ctl">
          <div
            class="rg-camera-ctl"
            v-show="showPTZCtlAndHasWriteControlCameraPrivilege"
          >
            <div class="rg-pan-tilt-panel">
              <span class="tilt-up-btn" @click="onClickPTZBtn(PTZ.tiltUp)"
                ><img src="@/assets/images/radarGrid/tilt_up.svg"
              /></span>
              <span class="pan-left-btn" @click="onClickPTZBtn(PTZ.panLeft)"
                ><img src="@/assets/images/radarGrid/pan_left.svg"
              /></span>
              <span class="pan-right-btn" @click="onClickPTZBtn(PTZ.panRight)"
                ><img src="@/assets/images/radarGrid/pan_right.svg"
              /></span>
              <span class="tilt-bottom-btn" @click="onClickPTZBtn(PTZ.tiltDown)"
                ><img src="@/assets/images/radarGrid/tilt_bottom.svg"
              /></span>
            </div>
            <div class="rg-zoom-panel">
              <span class="zoom-in-btn" @click="onClickPTZBtn(PTZ.zoomIn)"
                ><img src="@/assets/images/radarGrid/zoom_in.svg"
              /></span>
              <span class="zoom-out-btn" @click="onClickPTZBtn(PTZ.zoomOut)"
                ><img src="@/assets/images/radarGrid/zoom_out.svg"
              /></span>
            </div>
          </div>
        </transition>
        <div class="loader-wrap" v-show="isLoaderVisible">
          <div class="loader"></div>
        </div>
        <div v-show="isRtspPlayerErrorMessageVisible" class="rg-camera-error">
          <span class="aisea-text-10-400-white">{{ errorMsg }}</span>
        </div>
      </div>
      <div class="rg-camera-footer">
        <div class="rg-camera-live">
          <img src="@/assets/images/radarGrid/live.svg" />
        </div>
        <div class="rg-camera-ic">
          <img src="@/assets/images/radarGrid/video_fill.svg" />
        </div>
        <div class="rg-camera-name truncate">
          <span class="aisea-text-12-400-white truncate">{{
            aiseaCameraName
          }}</span>
        </div>
        <div class="rg-camera-switch" v-show="hasWriteControlCameraPrivilege">
          <div
            class="rg-camera-manual aisea-text-12-400-white"
            @click="onClickCameraSwitchBtn(CAMERA_CTL_STATUS.AUTO)"
            :style="cameraSwitchStyles.autoStyle"
          >
            <span>自動</span>
          </div>
          <div
            class="rg-camera-auto aisea-text-12-400-white"
            @click="onClickCameraSwitchBtn(CAMERA_CTL_STATUS.MANUAL)"
            :style="cameraSwitchStyles.manualStyle"
          >
            <span>手動</span>
          </div>
        </div>
      </div>
    </div>
  </draggable-resizer>
</template>

<script>
import { RadarGridMixin } from "@/mixins";
import { db } from "@/firebase/firebaseConfig";
import moment from "moment";
import Vue from "vue";
import _ from "lodash";
import RtspVideoPlayer from "./RtspVideoPlayer.vue";
import DraggableResizer from "./DraggableResizer.vue";
const CAMERA_MANUAL_TIMEOUT_MILLISECONDS = 5 * 60 * 1000;

export default {
  name: "RgCamera",
  mixins: [RadarGridMixin],
  components: {
    RtspVideoPlayer,
    DraggableResizer,
  },
  props: {
    elId: String,
    cameraId: String,
    uuid: String,
    rgInfo: Array,
    cameraHashMap: {
      type: Object,
      default: () => {
        return {};
      },
    },
    radarCameraMaster: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      connecting: true,
      connectingError: false,
      errorMsg: "接続に失敗しました",
      styleObject: {
        top: "50%",
        left: "50%",
      },
      cameraSwitchStatus: Number,
      cameraSwitchStyles: {
        manualStyle: {},
        autoStyle: {},
      },
      showPTZCtl: false,

      PTZ: {
        panLeft: "L",
        panRight: "R",
        tiltUp: "U",
        tiltDown: "D",
        zoomIn: "i",
        zoomOut: "o",
      },
      controlCameraPrivilegeMap: {},
      cameraManualTimeoutId: null,
      isRtspConfigReady: false,
    };
  },

  watch: {
    cameraSwitchStatus() {
      // カメラの再生モードがRTSPでなくなった場合、カメラの接続を切る
      if (!this.isRtspMode) {
        this.destroyRtspPlayer();
      }
    },

    isRtspMode() {
      if (this.isRtspMode) {
        this.setupRtspConnectionConfig();
      } else {
        this.destroyRtspPlayer();
      }
    },
  },

  computed: {
    isRtspPlayerVisible() {
      if (!this.isRtspValid) return false;
      return this.isRtspMode;
    },

    isLoaderVisible() {
      if (!this.isRtspConfigReady) return true;
      return (
        this.isRtspPlayerVisible && this.connecting && !this.connectingError
      );
    },

    isRtspPlayerErrorMessageVisible() {
      return !this.isRtspPlayerVisible && !this.isLoaderVisible;
    },

    rtspSrc() {
      if (!_.isObject(this.radarCameraMaster)) return "";
      return this.createRtspUrl(this.radarCameraMaster.cu) || "";
    },

    rtspCanvasId() {
      return `${this.cameraId}-rtsp`;
    },

    aiseaCameraId() {
      if (!_.isString(this.aiseaCameraHash)) return "";
      if (!this.cameraHashMap) return "";
      if (!Object.hasOwn(this.cameraHashMap, this.aiseaCameraHash)) return "";
      return this.cameraHashMap[this.aiseaCameraHash].id || "";
    },

    aiseaCameraHash() {
      if (!this.radarCameraMaster) return "";
      return this.radarCameraMaster.a || "";
    },

    aiseaCameraName() {
      if (!_.isString(this.aiseaCameraHash)) return "";
      if (!this.cameraHashMap) return "";
      if (!Object.hasOwn(this.cameraHashMap, this.aiseaCameraHash)) return "";
      return this.cameraHashMap[this.aiseaCameraHash].name || "";
    },

    isRtspValid() {
      return this.isRtspSrcValid && this.isRtspConfigReady;
    },

    isRtspSrcValid() {
      return (
        _.isString(this.rtspSrc) &&
        this.rtspSrc.trim() !== "" &&
        this.rtspSrc.startsWith("wss://")
      );
    },

    showPTZCtlAndHasWriteControlCameraPrivilege() {
      if (!this.showPTZCtl) return false;
      return this.hasWriteControlCameraPrivilege;
    },

    hasWriteControlCameraPrivilege() {
      return Object.prototype.hasOwnProperty.call(
        this.controlCameraPrivilegeMap,
        this.RG_PRIVILEGE_TYPES.WRITE,
      );
    },

    isManualMode() {
      return this.cameraSwitchStatus === this.CAMERA_CTL_STATUS.MANUAL;
    },

    isAutoMode() {
      return this.cameraSwitchStatus === this.CAMERA_CTL_STATUS.AUTO;
    },

    isCameraControlDisabled() {
      return this.cameraSwitchStatus === this.CAMERA_CTL_STATUS.DISABLED;
    },

    isRtspMode() {
      return (
        // 現状、カメラ接続は常にrtspになる
        this.isAutoMode || this.isManualMode || this.isCameraControlDisabled
      );
    },

    isRtspPlaying() {
      return this.isRtspMode && this.connecting === false;
    },
  },

  methods: {
    async validateRtspConfig() {
      try {
        this.connectingError = false;
        if (!this.isRtspValid) {
          this.errorMsg = "接続に失敗しました";
          throw new Error("接続に失敗しました");
        }
      } catch (error) {
        this.handleRtspPlayError(error);
      }
    },

    handleRtspPlayError(error) {
      this.connectingError = true;
      Vue.$log.debug(`Debug rtsp playVideo :`, error);
      this.errorMsg = "接続に失敗しました";
    },

    handleConnectionRestored() {
      this.connecting = false;
    },

    handleDisconnectDetection() {
      this.connecting = true;
    },

    playing() {
      this.connecting = false;
    },

    onClickCloseBtn() {
      this.$emit("onClickCloseBtn", this.cameraId);
    },

    onClickCameraSwitchBtn(status) {
      if (this.cameraSwitchStatus !== this.CAMERA_CTL_STATUS.DISABLED) {
        this.setCameraCtlSwitchStatus(status, true);
      }
    },

    onClickPTZBtn: _.throttle(function (key) {
      this.updateCameraMoveRequest(key);
    }, 1000),

    setCameraCtlSwitchStatus(
      status,
      doUpdateCameraCtl,
      doUpdateCameraFocus = false,
    ) {
      if (status === this.cameraSwitchStatus) return;
      if (status === this.CAMERA_CTL_STATUS.AUTO) {
        if (doUpdateCameraCtl) {
          this.updateCameraCtl(status);
        }
        if (doUpdateCameraFocus) {
          this.updateCameraFocusObject();
        }
        this.cameraSwitchStyles.autoStyle = this.CAMERA_SWITCH_ON_STYLE;
        this.cameraSwitchStyles.manualStyle = this.CAMERA_SWITCH_OFF_STYLE;
        this.cameraSwitchStatus = this.CAMERA_CTL_STATUS.AUTO;
        this.showPTZCtl = false;
      } else if (status === this.CAMERA_CTL_STATUS.MANUAL) {
        if (doUpdateCameraCtl) this.updateCameraCtl(status);
        this.cameraSwitchStyles.manualStyle = this.CAMERA_SWITCH_ON_STYLE;
        this.cameraSwitchStyles.autoStyle = this.CAMERA_SWITCH_OFF_STYLE;
        this.cameraSwitchStatus = this.CAMERA_CTL_STATUS.MANUAL;
        this.showPTZCtl = true;
        this.setCameraManualTimeout();
      } else {
        this.cameraSwitchStyles.manualStyle =
          this.CAMERA_SWITCH_OFF_DISABLED_STYLE;
        this.cameraSwitchStyles.autoStyle =
          this.CAMERA_SWITCH_ON_DISABLED_STYLE;
        this.cameraSwitchStatus = this.CAMERA_CTL_STATUS.DISABLED;
        this.showPTZCtl = false;
        this.clearCameraManualTimeout();
      }
    },

    async updateCameraCtl(status) {
      const docRef = db
        .collection(this.RG_FB_COLLECTIONS.AI_CAMERA_CONTROL)
        .doc(this.cameraId);
      if (status === this.CAMERA_CTL_STATUS.MANUAL) {
        const from = moment().utc();
        await docRef.update({
          p: this.uuid,
          fm: from.toDate(),
          to: from.add(CAMERA_MANUAL_TIMEOUT_MILLISECONDS, "ms").toDate(),
        });
      } else {
        docRef.get().then(async (doc) => {
          if (doc.data().p === this.uuid) {
            await docRef.update({
              p: null,
            });
          }
        });
      }
    },

    async updateCameraFocusObject() {
      if (!this.rgInfo) return; // 地上カメラをクリックしたときはした時はターゲットがないのでfocusしない
      this.rgInfo.forEach((val) => {
        db.collection(this.RG_FB_COLLECTIONS.CAMERA_FOCUS_REQUEST)
          .add({
            t: moment().utc().toDate(),
            p: this.uuid,
            r: val.r,
            tg: val.tg,
          })
          .catch((error) => {
            Vue.$log.error(
              `Error add document to ${this.RG_FB_COLLECTIONS.CAMERA_FOCUS_REQUEST} :`,
              error,
            );
          });
      });
    },

    async updateCameraMoveRequest(key) {
      db.collection(this.RG_FB_COLLECTIONS.CAMERA_MOVE_REQUEST)
        .add({
          t: moment().utc().toDate(),
          p: this.uuid,
          ci: this.cameraId,
          mv: key,
        })
        .catch((error) => {
          Vue.$log.error(
            `Error add document to ${this.RG_FB_COLLECTIONS.CAMERA_MOVE_REQUEST} :`,
            error,
          );
        });
    },

    createRtspUrl(cameraURL) {
      if (!_.isString(cameraURL)) return "";
      return cameraURL;
    },

    setRadarGridPrivilegeMap({ controlCameraPrivilegeMap }) {
      this.setControlCameraPrivilegeMap(controlCameraPrivilegeMap);
    },

    setControlCameraPrivilegeMap(controlCameraPrivilegeMap) {
      if (controlCameraPrivilegeMap) {
        this.controlCameraPrivilegeMap = controlCameraPrivilegeMap;
      }
    },

    setCameraManualTimeout() {
      this.clearCameraManualTimeout();
      this.cameraManualTimeoutId = window.setTimeout(() => {
        this.setCameraCtlSwitchStatus(this.CAMERA_CTL_STATUS.AUTO, true);
        this.clearCameraManualTimeout();
      }, CAMERA_MANUAL_TIMEOUT_MILLISECONDS);
    },

    async setupRtspConnectionConfig() {
      this.isRtspConfigReady = false;
      this.destroyRtspPlayer();
      this.connecting = true;
      this.$nextTick(async () => {
        setTimeout(() => {
          this.isRtspConfigReady = true;
          this.validateRtspConfig();
        }, 200); // URLが不正な時にもローディングを表示するために少し遅らせる
      });
    },

    clearCameraManualTimeout() {
      window.clearTimeout(this.cameraManualTimeoutId);
    },

    destroyRtspPlayer() {
      if (this.$refs.rtspVideoPlayer) {
        this.$refs.rtspVideoPlayer.$destroy();
      }
    },

    cleanup() {
      this.destroyRtspPlayer();
      this.setCameraCtlSwitchStatus(this.CAMERA_CTL_STATUS.AUTO, true);
    },
  },
  created() {
    this.setCameraCtlSwitchStatus(this.CAMERA_CTL_STATUS.DISABLED);
  },

  beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

.radar-grid-object-camera-container-wrapper {
  position: absolute;
  cursor: move;
}

.radar-grid-object-camera-container {
  position: absolute;
  z-index: 12;
  display: grid;
  align-items: center;
  width: 100%;
  height: 100%;
  grid-template-areas:
    ". rg-camera-header ."
    ". rg-camera-body ."
    ". rg-camera-footer .";
  grid-template-rows: 28px 1fr 38px;
  grid-template-columns: 14px 1fr 14px;
  background: #021431;
  border-radius: 5px;
  user-select: none;

  .close-btn {
    display: block;
    position: absolute;
    top: 2px;
    right: 8px;
    text-align: right;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #222222;
    cursor: pointer;
    user-select: none;

    &:hover {
      background-color: #333333;
      transition: background-color 0.3s ease;
      cursor: pointer;
    }
  }

  .close-btn::before,
  .close-btn::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2px;
    height: 12px;
    background: #fff;
  }

  .close-btn::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  .close-btn::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  .rg-camera-header {
    grid-area: rg-camera-header;
    height: 28px;
  }

  .rg-camera-body {
    position: relative;
    $rg-camera-body-height: 100%;
    $rg-camera-overlay-width: 100%;
    grid-area: rg-camera-body;
    background-color: #021431;
    border-radius: 5px;
    height: $rg-camera-body-height;
    display: flex;
    justify-content: center;
    align-items: center;

    .rg-camera-ctl {
      height: 100%;
      width: 100%;
      position: absolute;
      z-index: 1;
      .rg-pan-tilt-panel {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0px;
        display: grid;
        justify-items: center;
        align-items: center;
        grid-template-areas:
          ".... up ....."
          "left ...... right"
          ".... bottom .....";
        grid-template-rows: 1fr 1fr 1fr;
        grid-template-columns: 100px 1fr 100px;

        .tilt-up-btn {
          grid-area: up;
          align-self: start;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          background-color: #222222;
          border-radius: 50vh;
          margin-top: 8px;

          &:hover {
            background-color: #333333;
            transition: background-color 0.3s ease;
            cursor: pointer;
          }
        }

        .tilt-bottom-btn {
          grid-area: bottom;
          align-self: end;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          background-color: #222222;
          border-radius: 50vh;
          margin-bottom: 8px;

          &:hover {
            background-color: #333333;
            transition: background-color 0.3s ease;
            cursor: pointer;
          }
        }

        .pan-right-btn {
          grid-area: right;
          justify-self: end;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          background-color: #222222;
          border-radius: 50vh;
          margin-right: 8px;

          &:hover {
            background-color: #333333;
            transition: background-color 0.3s ease;
            cursor: pointer;
          }
        }

        .pan-left-btn {
          grid-area: left;
          justify-self: start;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          background-color: #222222;
          border-radius: 50vh;
          margin-left: 8px;

          &:hover {
            background-color: #333333;
            transition: background-color 0.3s ease;
            cursor: pointer;
          }
        }
      }

      .rg-zoom-panel {
        position: absolute;
        right: 18px;
        bottom: 8px;
        display: grid;
        grid-template-areas: "zoom-out-btn zoom-in-btn";
        justify-items: center;
        align-items: center;
        grid-template-rows: auto;
        grid-template-columns: 34px 34px;

        .zoom-in-btn {
          grid-area: zoom-in-btn;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          background-color: #bdbdbd;
          border-radius: 50vh;

          &:hover {
            background-color: #999999;
            transition: background-color 0.3s ease;
            cursor: pointer;
          }
        }

        .zoom-out-btn {
          grid-area: zoom-out-btn;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          background-color: #bdbdbd;
          border-radius: 50vh;

          &:hover {
            background-color: #999999;
            transition: background-color 0.3s ease;
            cursor: pointer;
          }
        }
      }
    }

    .loader-wrap {
      background-color: transparent;
      border-radius: 5px;
      height: $rg-camera-body-height;
      width: $rg-camera-overlay-width;
      position: absolute;
      background-color: #222222;
    }

    .loader {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #fff;
      box-shadow: 16px 0 #fff, -16px 0 #fff;
      position: absolute;
      top: 50%;
      left: 50%;
      margin: 0 0 0 0;
      animation: flash 0.5s ease-out infinite alternate;
    }

    @keyframes flash {
      0% {
        background-color: #fff2;
        box-shadow: 16px 0 #fff2, -16px 0 #fff;
      }
      50% {
        background-color: #fff;
        box-shadow: 16px 0 #fff2, -16px 0 #fff2;
      }
      100% {
        background-color: #fff2;
        box-shadow: 16px 0 #fff, -16px 0 #fff2;
      }
    }

    .rg-camera-error {
      position: absolute;
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #222222;
      span {
        max-width: 100%;
        word-wrap: break-word;
      }
    }
  }

  .rg-camera-footer {
    grid-area: rg-camera-footer;
    display: grid;
    align-items: center;
    grid-template-areas: "rg-camera-live rg-camera-ic rg-camera-name rg-camera-switch";
    grid-template-rows: auto;
    grid-template-columns: 48px 18px 300px 1fr;

    .rg-camera-live {
      grid-area: rg-camera-live;
      width: 28px;
      height: 21px;
    }

    .rg-camera-ic {
      grid-area: rg-camera-ic;
      margin-top: 2px;
    }

    .rg-camera-name {
      grid-area: rg-camera-name;
    }

    .rg-camera-switch {
      grid-area: rg-camera-switch;
      justify-self: end;
      border-radius: 5px;
      display: grid;
      grid-template-areas: "rg-camera-manual rg-camera-auto";
      grid-template-rows: 20px;
      grid-template-columns: 40px 40px;

      .rg-camera-manual {
        border-radius: 3px 0 0 3px;
        grid-area: rg-camera-manual;
        background-color: #2d9cdb;

        span {
          margin-left: 8px;
        }

        &:hover {
          cursor: pointer;
        }
      }

      .rg-camera-auto {
        grid-area: rg-camera-auto;
        border-radius: 0 3px 3px 0;
        background-color: #6c7178;

        span {
          margin-left: 8px;
        }

        &:hover {
          cursor: pointer;
        }
      }
    }
  }

  .camera-ctl-enter-active,
  .camera-ctl-leave-active {
    transition: opacity 0.8s ease;
  }

  .camera-ctl-enter-from,
  .camera-ctl-leave-to {
    opacity: 0;
  }
}
</style>
